// ============================================
// Firestore Regulatory Service
// CRUD for regulatory alerts, policies, and notification campaigns
// ============================================

import {
  collection, doc, getDocs, getDoc, setDoc, deleteDoc,
  writeBatch, orderBy, query,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { RegulatoryAlert, RegulatoryPolicy, NotificationCampaign } from '../../types/regulatory';

import { C_REGULATORY_ALERTS, C_REGULATORY_CAMPAIGNS, C_REGULATORY_POLICIES } from '../../constants/collections';
// --- Collection refs ---

const alertsCol = () => collection(db, C_REGULATORY_ALERTS);
const policiesCol = () => collection(db, C_REGULATORY_POLICIES);
const campaignsCol = () => collection(db, C_REGULATORY_CAMPAIGNS);

// --- Alerts ---

export async function getAllAlerts(): Promise<RegulatoryAlert[]> {
  const snap = await getDocs(query(alertsCol(), orderBy('relevance', 'desc')));
  return snap.docs.map((d) => d.data() as RegulatoryAlert);
}

export async function getAlert(id: string): Promise<RegulatoryAlert | null> {
  const snap = await getDoc(doc(alertsCol(), id));
  return snap.exists() ? (snap.data() as RegulatoryAlert) : null;
}

export async function saveAlert(alert: RegulatoryAlert): Promise<void> {
  await setDoc(doc(alertsCol(), alert.id), stripUndefined(alert));
}

export async function deleteAlert(id: string): Promise<void> {
  await deleteDoc(doc(alertsCol(), id));
}

// --- Policies ---

export async function getAllPolicies(): Promise<RegulatoryPolicy[]> {
  const snap = await getDocs(policiesCol());
  return snap.docs.map((d) => d.data() as RegulatoryPolicy);
}

export async function savePolicy(policy: RegulatoryPolicy): Promise<void> {
  await setDoc(doc(policiesCol(), policy.id), stripUndefined(policy));
}

// --- Campaigns ---

export async function getAllCampaigns(): Promise<NotificationCampaign[]> {
  const snap = await getDocs(campaignsCol());
  return snap.docs.map((d) => d.data() as NotificationCampaign);
}

export async function saveCampaign(campaign: NotificationCampaign): Promise<void> {
  await setDoc(doc(campaignsCol(), campaign.id), stripUndefined(campaign));
}

export async function deleteCampaign(id: string): Promise<void> {
  await deleteDoc(doc(campaignsCol(), id));
}

// --- Seed ---

export async function seedRegulatoryAlerts(
  alerts: RegulatoryAlert[],
  onProgress?: (msg: string) => void,
): Promise<number> {
  const batchSize = 450;

  for (let i = 0; i < alerts.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = alerts.slice(i, i + batchSize);
    for (const alert of chunk) {
      batch.set(doc(alertsCol(), alert.id), stripUndefined(alert));
    }
    await batch.commit();
    onProgress?.(`Wrote batch ${Math.floor(i / batchSize) + 1} (${chunk.length} alerts)`);
  }

  return alerts.length;
}

export async function seedRegulatoryAll(
  alerts: RegulatoryAlert[],
  policies: RegulatoryPolicy[],
  campaigns: NotificationCampaign[],
  onProgress?: (msg: string) => void,
): Promise<{ alerts: number; policies: number; campaigns: number }> {
  const batch = writeBatch(db);

  for (const alert of alerts) {
    batch.set(doc(alertsCol(), alert.id), stripUndefined(alert));
  }
  for (const policy of policies) {
    batch.set(doc(policiesCol(), policy.id), stripUndefined(policy));
  }
  for (const campaign of campaigns) {
    batch.set(doc(campaignsCol(), campaign.id), stripUndefined(campaign));
  }

  await batch.commit();
  onProgress?.(`Wrote ${alerts.length} alerts, ${policies.length} policies, ${campaigns.length} campaigns`);

  return { alerts: alerts.length, policies: policies.length, campaigns: campaigns.length };
}

// --- Helpers ---

/** Strip undefined values from an object (Firestore rejects undefined) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object') ? stripUndefined(value) : value;
    }
  }
  return cleaned;
}
